import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { enumCompanyPaymentType } from '@data-layer/enums/companies';
import { ManagementEntityEventModel } from '@data-layer/models/management';
import { DependenciesRepository, IsfMessagesRepository } from '@data-layer/repositories';
import { enumIsfState } from '@data-layer/enums/isf';

import { enumIsfCodeStep } from '@app/modules/isf/enums';
import { WidgetsService } from '@services/widgets.service';
import { ISF_CBP_MESSAGES_MAP } from '@constants/isf-sbp-messages-map';

import { IsfHelper } from '@helpers/isf.helper';
import { enumEntityByKey } from '@data-layer/enums/entities';
import { GenericService } from '@services/generic.service';
import { IsfValidationService } from '@services/isf-validation.service';
import { DESC_ENTITY } from '@constants/desc-entity';
import { IsfModel } from '@data-layer/models/isf';
import { ReplaySubject } from 'rxjs';
import { IsfCBPMessagesService } from '@shared/cbp-messages/services/isf-cbp-messages.service';

@Component({
  selector: 'isf-card-notice',
  templateUrl: './isf-card-notice.component.html',
  styleUrls: ['./isf-card-notice.component.scss'],
})
export class IsfCardNoticeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public isf: IsfModel;
  @Input() public userPaymentType: enumCompanyPaymentType = enumCompanyPaymentType.INSTANT;
  @Input() public showOpenDocumentLink: boolean = true;
  @Input() public showContactTeem: boolean = true;
  @Input() public openDocuments: boolean = true;
  @Input() public showCustomStateTooltip: boolean = false;
  @Input() public showHeaderOnDraft: boolean = false;
  @Output() public openDocumentsChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public goToEdit: EventEmitter<enumIsfCodeStep> = new EventEmitter<enumIsfCodeStep>();
  @Output() public messagesLoaded: EventEmitter<any> = new EventEmitter<any>();

  public cardBodyText = null;
  public cardHeaderAttention = null;
  public cardBodyAttention = null;
  public state: enumIsfState;
  public enumIsfState: typeof enumIsfState = enumIsfState;
  public isShowSupport = null;
  public cbpMessages: any[] = [];
  public messages: any[] = [];
  public cbpLogMessages: ManagementEntityEventModel[] = [];
  public isShowLoader: boolean = false;
  public isShowAttention: boolean = false;
  public isShowNotice: boolean = true;
  public isShowCbpLog: boolean = false;
  public isShowCbpLogLoader: boolean = false;
  public info = DESC_ENTITY.SUM;

  private readonly cbpMessageStates = [
    enumIsfState.REJECTED_CBP,
    enumIsfState.NO_BILL_ON_FILE,
    enumIsfState.BILL_TYPE_NOT_MATCHED,
    enumIsfState.APPROVED,
  ];

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private widgetsService: WidgetsService,
    private translate: TranslateService,
    private dependenciesRepository: DependenciesRepository,
    private genericService: GenericService,
    private isfMessagesRepository: IsfMessagesRepository,
    private isfValidationService: IsfValidationService,
    private cbpMessagesService: IsfCBPMessagesService,
  ) {}

  public ngOnInit(): void {
    this.cbpMessagesService.isShowLoader$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isShowLoader = val));
    this.cbpMessagesService.isShowCbpLogLoader$.pipe(takeUntil(this.destroy$)).subscribe((val: boolean) => (this.isShowCbpLogLoader = val));
    this.cbpMessagesService.cbpLogMessages$
      .pipe(takeUntil(this.destroy$))
      .subscribe((val: ManagementEntityEventModel[]) => (this.cbpLogMessages = val));
    this.cbpMessagesService.messages$.pipe(takeUntil(this.destroy$)).subscribe((val: string[]) => (this.messages = val));
    this.cbpMessagesService.messagesLoaded$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.messagesLoaded.emit();
    });
  }

  public ngOnChanges(): void {
    this.state = this.isf.state;
    this.cbpMessagesService.initData(this.isf);
    this.checkShowNotice();
    this.getShowAction();
    this.getBodyText();

    const isLG: boolean = !!IsfHelper.getIsfShipmentEntityById(this.isf, enumEntityByKey.STAFFING_LOCATION);
    const isCS: boolean = !!IsfHelper.getIsfShipmentEntityById(this.isf, enumEntityByKey.CONSOLIDATOR);
    if (!isLG || !isCS) {
      this.isShowAttention = true;
      this.cardHeaderAttention = 'INFO.WARNING';
      this.cardBodyAttention = !isLG && !isCS ? 'INFO.CS_LS_ATTENTION' : !isLG ? 'INFO.LG.IS_ATTENTION_3' : 'INFO.CS.IS_ATTENTION_3';
    }
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public get isShowCbpLogButton(): boolean {
    return this.isf.state !== enumIsfState.NOT_SENT && this.cbpLogMessages.length > 0;
  }

  public get isShowCustomStateTooltip(): boolean {
    return (
      this.showCustomStateTooltip &&
      this.isf.state === enumIsfState.CUSTOM &&
      !!this.isf.customState &&
      !!this.isf.customState.internalNotes
    );
  }

  public toggleCbpLog(): void {
    this.isShowCbpLog = !this.isShowCbpLog;
  }

  public showTranslatedKey(message: any): boolean {
    return this.cbpMessagesService.showTranslatedKey(message);
  }

  public registerMessage(message: any): string {
    return this.cbpMessagesService.registerMessage(message);
  }

  public getEntityTypeText(message: any): string {
    return this.cbpMessagesService.getEntityTypeText(message);
  }

  public goToUpdateLS() {
    const isLG: boolean = !!this.isf?.shipment?.stuffingLocationId;
    const isCS: boolean = !!this.isf?.shipment?.consolidatorId;

    if (!isLG) {
      this.goToEdit.emit(enumIsfCodeStep.LG);
    } else if (!isCS) {
      this.goToEdit.emit(enumIsfCodeStep.CS);
    }
  }

  public get showText(): boolean {
    const isErrorState =
      this.cbpMessageStates.includes(this.isf.state) ||
      [enumIsfState.NOT_VALID_AT_PROVIDER, enumIsfState.UNRECOVERABLE].includes(this.isf.state);
    const isHasErrorMessages =
      this.cbpMessages.length > 0 || !!(this.isf.shipment && this.isf.shipment.validationMessages['referenceNumber']);

    return isErrorState ? !!this.cardBodyText && isHasErrorMessages : !!this.cardBodyText;
  }

  public contactToSupport(): void {
    const { referenceNumber } = this.isf;
    this.widgetsService.trackData('Contact team:', { referenceNumber });
    this.widgetsService.openWidget(['isf']);
  }

  public messageClick(message: any): void {
    const codeStep: enumIsfCodeStep = this.getCodeStepForMessage(message.initialMessage);
    if (codeStep) {
      this.goToEdit.emit(codeStep);
    }
  }

  public getCodeStepForMessage(message: any): enumIsfCodeStep {
    if (message && message.key && ISF_CBP_MESSAGES_MAP[message.key]) {
      const stepCode: enumIsfCodeStep = ISF_CBP_MESSAGES_MAP[message.key](message.params);
      if (stepCode) {
        return stepCode;
      }
    }
  }

  public get isDeletedStatus(): boolean {
    return [enumIsfState.DELETED, enumIsfState.CBP_DELETED].includes(this.isf.state);
  }

  private checkShowNotice(): void {
    this.isShowNotice = this.isf.state !== enumIsfState.NOT_SENT;
  }

  private getBodyText(): void {
    switch (this.isf.state) {
      case enumIsfState.BOND_PENDING:
        this.cardBodyText = 'STATUS.CARD_TEXT.BOND_PENDING';
        break;
      case enumIsfState.DRAFT_CBY_V_SEND:
      case enumIsfState.VALIDATED_AT_PROVIDER:
      case enumIsfState.PROVIDER_SEND_PENDING:
        this.cardBodyText = 'STATUS.CARD_TEXT.DRAFT_CBY_V_SEND';
        break;
      case enumIsfState.NOT_VALID_AT_PROVIDER:
      case enumIsfState.UNRECOVERABLE:
        this.cardBodyText = 'STATUS_ES.DESCRIPTION.UNRECOVERABLE';
        break;
      case enumIsfState.DRAFT_CBP_PEND:
        this.cardBodyText = 'STATUS.CARD_TEXT.DRAFT_CBP_PEND';
        break;
      case enumIsfState.REJECTED_CBP:
        this.cardBodyText = 'STATUS.CARD_TEXT.REJECTED_CBP';
        break;
      case enumIsfState.ACCEPTED_CBP:
        this.cardBodyText = 'STATUS.CARD_TEXT.DRAFT_CBP_PEND';
        break;
      case enumIsfState.NO_BILL_ON_FILE:
        this.cardBodyText = 'STATUS.CARD_TEXT.NO_BILL_ON_FILE';
        break;
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
        this.cardBodyText = 'STATUS.CARD_TEXT.BILL_TYPE_NOT_MATCHED';
        break;
      case enumIsfState.APPROVED:
        this.cardBodyText = 'STATUS.CARD_TEXT.APPROVED_ISF';
        break;
      case enumIsfState.CBP_DELETE_PENDING:
        this.cardBodyText = 'STATUS.CARD_TEXT.REMOVAL_PENDING';
        break;
      case enumIsfState.CBP_DELETED:
        this.cardBodyText = 'STATUS.CARD_TEXT.CBP_DELETED';
        break;
      case enumIsfState.DELETED:
        this.cardBodyText = 'STATUS.CARD_TEXT.DELETED';
        break;
      case enumIsfState.CUSTOM:
        this.cardBodyText = this.isf.customState.description;
        break;
      default:
        this.cardBodyText = null;
        break;
    }
  }

  private getShowAction(): void {
    switch (this.isf.state) {
      case enumIsfState.NOT_VALID_AT_PROVIDER:
      case enumIsfState.UNRECOVERABLE:
      case enumIsfState.REJECTED_CBP:
      case enumIsfState.NO_BILL_ON_FILE:
      case enumIsfState.BILL_TYPE_NOT_MATCHED:
        this.isShowSupport = true;
        break;
      default:
        this.isShowSupport = false;
        break;
    }
  }
}
